import { ContentRect } from './drag-selection.types';

export interface ViewportPoint {
  x: number;
  y: number;
}

export interface ContentPoint {
  contentX: number;
  contentY: number;
}

export class ContentRectTracker {
  private viewport: HTMLElement | null = null;
  private viewportRect: DOMRect | null = null;
  private anchor: ContentPoint | null = null;
  private lastPointer: ViewportPoint | null = null;

  public attach(viewport: HTMLElement): void {
    this.viewport = viewport;
    this.viewportRect = viewport.getBoundingClientRect();
  }

  public refreshViewportRect(): void {
    if (!this.viewport) return;
    this.viewportRect = this.viewport.getBoundingClientRect();
  }

  public start(event: MouseEvent): ContentPoint | null {
    if (!this.viewport || !this.viewportRect) return null;
    const viewportPoint = this.toViewportPoint(event);
    const contentPoint = this.toContentPoint(viewportPoint);
    this.anchor = contentPoint;
    this.lastPointer = viewportPoint;
    return contentPoint;
  }

  public update(event: MouseEvent): ContentRect | null {
    if (!this.anchor || !this.viewport) return null;
    this.lastPointer = this.toViewportPoint(event);
    return this.computeRect();
  }

  public getCurrentRect(): ContentRect | null {
    if (!this.anchor || !this.lastPointer || !this.viewport) return null;
    return this.computeRect();
  }

  public getViewportPointer(): ViewportPoint | null {
    return this.lastPointer;
  }

  public hasAnchor(): boolean {
    return this.anchor !== null;
  }

  public reset(): void {
    this.anchor = null;
    this.lastPointer = null;
  }

  private computeRect(): ContentRect | null {
    if (!this.anchor || !this.lastPointer || !this.viewport) return null;
    const current = this.toContentPoint(this.lastPointer);
    return {
      top: Math.min(this.anchor.contentY, current.contentY),
      left: Math.min(this.anchor.contentX, current.contentX),
      width: Math.abs(current.contentX - this.anchor.contentX),
      height: Math.abs(current.contentY - this.anchor.contentY),
    };
  }

  private toViewportPoint(event: MouseEvent): ViewportPoint {
    const rect = this.viewportRect as DOMRect;
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  private toContentPoint(viewportPoint: ViewportPoint): ContentPoint {
    const viewport = this.viewport as HTMLElement;
    return {
      contentX: viewportPoint.x + viewport.scrollLeft,
      contentY: viewportPoint.y + viewport.scrollTop,
    };
  }
}
